
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from 'recharts';
import { TrendingUp, Target, BarChart3, PieChart as PieChartIcon, Radar as RadarIcon } from 'lucide-react';

interface PerformanceChartsProps {
  data: {
    distanceTrends: {
      data: Array<{
        date: string;
        distance: number;
        quality: number;
      }>;
    };
    consistencyTrends: {
      data: Array<{
        date: string;
        consistency: number;
      }>;
    };
    shotTypeBreakdown: {
      data: Array<{
        type: string;
        count: number;
        percentage: number;
        avgDistance: number;
      }>;
    };
    weeklyProgress: {
      data: Array<{
        week: string;
        sessions: number;
        avgQuality: number;
        improvement: number;
      }>;
    };
    techniqueRadar: {
      data: Array<{
        skill: string;
        value: number;
        maxValue: number;
      }>;
    };
  };
  timeRange: string;
}

const COLORS = {
  primary: '#0ea5e9',
  secondary: '#10b981',
  accent: '#f59e0b',
  success: '#22c55e',
  warning: '#eab308',
  danger: '#ef4444',
  muted: '#6b7280'
};

const PIE_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export function PerformanceCharts({ data, timeRange: _timeRange }: PerformanceChartsProps) {
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Custom tooltip for line charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-medium">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(1)}{entry.name.includes('Distance') ? 'm' : entry.name.includes('Quality') ? '/10' : '%'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid gap-6">
      {/* Distance and Quality Trends */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Distance & Quality Trends
            </CardTitle>
            <CardDescription>
              Shot distance and quality over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.distanceTrends.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#6b7280"
                />
                <YAxis yAxisId="distance" orientation="left" stroke="#6b7280" />
                <YAxis yAxisId="quality" orientation="right" stroke="#6b7280" />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  yAxisId="distance"
                  type="monotone" 
                  dataKey="distance" 
                  stroke={COLORS.primary} 
                  strokeWidth={2}
                  name="Distance"
                  dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
                />
                <Line 
                  yAxisId="quality"
                  type="monotone" 
                  dataKey="quality" 
                  stroke={COLORS.secondary} 
                  strokeWidth={2}
                  name="Quality"
                  dot={{ fill: COLORS.secondary, strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-green-600" />
              Consistency Trends
            </CardTitle>
            <CardDescription>
              Shot consistency percentage over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.consistencyTrends.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#6b7280"
                />
                <YAxis stroke="#6b7280" />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="consistency" 
                  stroke={COLORS.secondary} 
                  fill={COLORS.secondary}
                  fillOpacity={0.3}
                  strokeWidth={2}
                  name="Consistency"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Shot Type Breakdown and Weekly Progress */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-purple-600" />
              Shot Type Breakdown
            </CardTitle>
            <CardDescription>
              Distribution of shot types practiced
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data.shotTypeBreakdown.data}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ type, percentage }) => `${type}: ${percentage.toFixed(1)}%`}
                    labelLine={false}
                  >
                    {data.shotTypeBreakdown.data.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              
              <div className="space-y-2">
                {data.shotTypeBreakdown.data.map((shot, index) => (
                  <div key={shot.type} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                      />
                      <span>{shot.type}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{shot.count} shots</div>
                      <div className="text-muted-foreground">{shot.avgDistance.toFixed(1)}m avg</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-600" />
              Weekly Progress
            </CardTitle>
            <CardDescription>
              Session frequency and improvement rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.weeklyProgress.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" stroke="#6b7280" />
                <YAxis yAxisId="sessions" orientation="left" stroke="#6b7280" />
                <YAxis yAxisId="improvement" orientation="right" stroke="#6b7280" />
                <Tooltip 
                  formatter={(value, name) => [
                    `${value}${name === 'improvement' ? '%' : ''}`, 
                    name === 'sessions' ? 'Sessions' : name === 'avgQuality' ? 'Avg Quality' : 'Improvement'
                  ]}
                />
                <Bar 
                  yAxisId="sessions"
                  dataKey="sessions" 
                  fill={COLORS.primary} 
                  radius={[4, 4, 0, 0]}
                  name="sessions"
                />
                <Line 
                  yAxisId="improvement"
                  type="monotone" 
                  dataKey="improvement" 
                  stroke={COLORS.accent} 
                  strokeWidth={3}
                  name="improvement"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Technique Radar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RadarIcon className="h-5 w-5 text-indigo-600" />
            Technique Analysis
          </CardTitle>
          <CardDescription>
            Breakdown of batting technique components
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={data.techniqueRadar.data}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="skill" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <PolarRadiusAxis 
                  angle={90} 
                  domain={[0, 100]} 
                  tick={{ fill: '#6b7280', fontSize: 10 }}
                />
                <Radar 
                  name="Current Level" 
                  dataKey="value" 
                  stroke={COLORS.primary} 
                  fill={COLORS.primary} 
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
                <Tooltip 
                  formatter={(value) => [`${value}/100`, 'Score']}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
            
            <div className="space-y-4">
              <h4 className="font-semibold text-lg">Technique Breakdown</h4>
              {data.techniqueRadar.data.map((skill) => (
                <div key={skill.skill} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{skill.skill}</span>
                    <span className="text-sm font-medium">{skill.value}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${skill.value}%` }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {skill.value >= 85 ? 'Excellent' : 
                     skill.value >= 75 ? 'Good' : 
                     skill.value >= 65 ? 'Average' : 
                     'Needs Work'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}